import React, { useState, useEffect, useRef, useCallback } from "react"
import { useHistory } from "react-router-dom"
import {
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap"

import { useFormik } from "formik"

import toastHandler from "toastHandler"

import {
  postEvaluacionNoCliente,
  getChecklistByCode,
  getEstadosItemList,
  solicitarOfertaEvaluacion,
} from "api"
import refsStore from "refsStore"

import { TITULAR_FIELDS } from "./Titular.constants"

import FormWizard from "react-form-wizard-component"
import "react-form-wizard-component/dist/style.css"

// Componentes de cada paso
import Titular from "./Titular"
import Documentacion from "./Documentacion"
import KYC from "./KYC"
import Evaluacion from "./Evaluacion"
import Firmantes from "./Firmantes"
import Garantias from "./Garantias"
import Aprobacion from "./Aprobacion"
import ModalBiometria from "./ModalBiometria"

// API de persistencia de progreso
import {
  guardarProgresoOnboarding,
  recuperarProgresoOnboarding,
  marcarProgresoCompletado,
  descartarProgresoOnboarding,
} from "api/onboardingProgresoApi"

// Hook de tracking de funnel
import useFunnelTracking from "hooks/useFunnelTracking"

import "./themify-icons.css"
import "./onboarding-theme.css"

// ============================================
// Constantes
// ============================================

const NOMBRES_PASOS = [
  "Identificación",
  "Documentación",
  "KYC / AML",
  "Evaluación",
  "Firmantes",
  "Garantías",
  "Aprobación",
]

const INTERVALO_AUTOGUARDADO = 30000 // 30 segundos

// ============================================
// Componente Modal Recuperar Progreso
// ============================================

const ModalRecuperarProgreso = ({
  isOpen,
  toggle,
  progreso,
  onRecuperar,
  onDescartar,
  cargando,
}) => {
  if (!progreso) return null

  const formatearFecha = fecha => {
    if (!fecha) return "Desconocida"
    const date = new Date(fecha)
    return date.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const calcularTiempoTranscurrido = fecha => {
    if (!fecha) return ""
    const ahora = new Date()
    const fechaGuardado = new Date(fecha)
    const diffMs = ahora - fechaGuardado
    const diffMins = Math.floor(diffMs / 60000)
    const diffHoras = Math.floor(diffMins / 60)
    const diffDias = Math.floor(diffHoras / 24)

    if (diffDias > 0) return `hace ${diffDias} día${diffDias > 1 ? "s" : ""}`
    if (diffHoras > 0)
      return `hace ${diffHoras} hora${diffHoras > 1 ? "s" : ""}`
    if (diffMins > 0) return `hace ${diffMins} minuto${diffMins > 1 ? "s" : ""}`
    return "hace unos momentos"
  }

  const nombrePaso =
    progreso.nombrePaso ||
    NOMBRES_PASOS[progreso.pasoActual] ||
    `Paso ${(progreso.pasoActual || 0) + 1}`

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md" backdrop="static">
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="d-flex align-items-center">
          <i
            className="bx bx-refresh text-primary me-2"
            style={{ fontSize: 24 }}
          />
          <span>Progreso Guardado Encontrado</span>
        </div>
      </ModalHeader>

      <ModalBody className="pt-2">
        <Alert color="info" className="mb-3">
          <div className="d-flex align-items-start">
            <i className="bx bx-file me-2 mt-1" style={{ fontSize: 20 }} />
            <div>
              <strong>Se encontró un onboarding en progreso</strong>
              <p className="mb-0 mt-1 small">
                Puedes continuar donde lo dejaste o empezar de nuevo.
              </p>
            </div>
          </div>
        </Alert>

        <div className="bg-light rounded p-3 mb-3">
          <div className="row">
            <div className="col-6">
              <small className="text-muted d-block">Último paso</small>
              <strong>{nombrePaso}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Guardado</small>
              <strong>
                {calcularTiempoTranscurrido(progreso.fechaModificacion)}
              </strong>
            </div>
          </div>

          {progreso.fechaModificacion && (
            <div className="mt-2 pt-2 border-top">
              <small className="text-muted d-flex align-items-center">
                <i className="bx bx-time-five me-1" />
                {formatearFecha(progreso.fechaModificacion)}
              </small>
            </div>
          )}
        </div>

        {progreso.datosFormulario?.titular?.razonSocial && (
          <div className="border rounded p-2 mb-3">
            <small className="text-muted d-block">Empresa</small>
            <span>{progreso.datosFormulario.titular.razonSocial}</span>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button
          color="outline-secondary"
          onClick={onDescartar}
          disabled={cargando}
        >
          Empezar de Nuevo
        </Button>
        <Button color="primary" onClick={onRecuperar} disabled={cargando}>
          {cargando ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Cargando...
            </>
          ) : (
            "Continuar donde lo dejé"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================
// Componente Principal
// ============================================

export default function OnboardingWizard() {
  const history = useHistory()
  const setError = toastHandler(state => state.setError)
  const setSuccess = toastHandler(state => state.setSuccess)

  // ============================================
  // Hook de tracking de funnel
  // ============================================

  const tracking = useFunnelTracking({
    nombresPasos: NOMBRES_PASOS,
  })

  // ============================================
  // Estados principales
  // ============================================

  const [saving, setSaving] = useState(false)
  const [isCuitFounded, setIsCuitFounded] = useState(false)
  const [datosDB, setDatosDB] = useState(null)
  const [solicitud, setSolicitud] = useState({})

  // Estados por paso - Documentación
  const [documentosEstado, setDocumentosEstado] = useState({})
  const [documentacionCompleta, setDocumentacionCompleta] = useState(false)

  // Estados por paso - KYC
  const [kycCompletado, setKYCCompletado] = useState(false)
  const [resultadoKYC, setResultadoKYC] = useState(null)

  // Estados por paso - Evaluación
  const [evaluacionCompleta, setEvaluacionCompleta] = useState(null)
  const [calificacionElegida, setCalificacionElegida] = useState(null)

  // Estados por paso - Firmantes
  const [firmantes, setFirmantes] = useState([])
  const [estadosBiometria, setEstadosBiometria] = useState({})
  const [firmantesCompletos, setFirmantesCompletos] = useState(false)

  // Estados por paso - Garantías
  const [garantias, setGarantias] = useState([])
  const [garantiasCompletas, setGarantiasCompletas] = useState(false)

  // Estados por paso - Aprobación
  const [aprobaciones, setAprobaciones] = useState({})

  const [modalBiometria, setModalBiometria] = useState({
    open: false,
    firmante: null,
  })

  // ============================================
  // Estados de persistencia de progreso
  // ============================================

  const [pasoActual, setPasoActual] = useState(0)
  const [guardandoProgreso, setGuardandoProgreso] = useState(false)
  const [ultimoGuardado, setUltimoGuardado] = useState(null)
  const [mostrarModalRecuperar, setMostrarModalRecuperar] = useState(false)
  const [progresoRecuperado, setProgresoRecuperado] = useState(null)
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false)

  // Refs para control de ejecución
  const guardadoEnProgresoRef = useRef(false)
  const recuperacionRealizadaRef = useRef(false)
  const autoguardadoTimerRef = useRef(null)
  const formWizardRef = useRef(null)
  const trackingIniciadoRef = useRef(false)

  // ============================================
  // Referencias (stores)
  // ============================================

  const provincias = refsStore(state => state.provincias)
  const actividad = refsStore(state => state.actividad)
  const personeria = refsStore(state => state.personeria)
  const referidos = refsStore(state => state.referidos)
  const condicionIva = refsStore(state => state.condicionIva)
  const asesorComercial = refsStore(state => state.asesorComercial)
  const actividadEspecifica = refsStore(state => state.actividadEspecifica)
  const situacionBCRA = refsStore(state => state.situacionBCRA)
  const tamanioEmpresa = refsStore(state => state.tamanioEmpresa)
  const tipoCanalComercializacion = refsStore(
    state => state.tipoCanalComercializacion,
  )
  const sectorEconomico = refsStore(state => state.sectorEconomico)
  const originador = refsStore(state => state.originador)

  // ============================================
  // Formik
  // ============================================

  const generateInitialValues = formValues => {
    return {
      titular: {
        ...formValues.reduce((acc, item) => ({ ...acc, [item.value]: "" }), {}),
        composicionSocietaria: [],
        descripcionDeLosClientes: "",
        descripcionDeProveedores: "",
        breveResenaEmpresa: "",
        deudaBancariaNosis: "",
        tipoSociedad: "",
      },
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: generateInitialValues(TITULAR_FIELDS),
    onSubmit: () => {},
  })

  const { values, setValues } = formik

  // ============================================
  // Inicialización y tracking de inicio
  // ============================================

  useEffect(() => {
    // Trackear inicio del onboarding
    if (!trackingIniciadoRef.current) {
      trackingIniciadoRef.current = true
      tracking.trackInicio()
    }

    getEstadosItemList()
      .then(estados => {
        getChecklistByCode("2")
          .then(res => {
            setSolicitud({
              ...solicitud,
              checklistItems: res.map(item => ({
                ...item,
                estado: estados?.[0].codigo,
                falta: new Date(),
                fmodif: new Date(),
              })),
            })
          })
          .catch(setError)
      })
      .catch(setError)
  }, [])

  // ============================================
  // Funciones de persistencia de progreso
  // ============================================

  const guardarProgreso = useCallback(
    async (forzar = false) => {
      if (guardadoEnProgresoRef.current && !forzar) return

      const cuit = values?.titular?.cuit
      if (!cuit || cuit.length < 11) return

      try {
        guardadoEnProgresoRef.current = true
        setGuardandoProgreso(true)

        const datosAdicionales = {
          documentosEstado,
          kycCompletado,
          resultadoKYC,
          evaluacionCompleta,
          calificacionElegida,
          firmantes,
          estadosBiometria,
          firmantesCompletos,
          garantias,
          garantiasCompletas,
          aprobaciones,
          isCuitFounded,
        }

        const datos = {
          cuit: cuit.replace(/[^0-9]/g, ""),
          pasoActual,
          nombrePaso: NOMBRES_PASOS[pasoActual],
          datosFormulario: values,
          datosAdicionales,
        }

        const resultado = await guardarProgresoOnboarding(datos)

        if (resultado.success) {
          setUltimoGuardado(new Date())
        }
      } catch (error) {
        console.error("Error guardando progreso:", error)
      } finally {
        guardadoEnProgresoRef.current = false
        setGuardandoProgreso(false)
      }
    },
    [
      values,
      pasoActual,
      documentosEstado,
      kycCompletado,
      resultadoKYC,
      evaluacionCompleta,
      calificacionElegida,
      firmantes,
      estadosBiometria,
      firmantesCompletos,
      garantias,
      garantiasCompletas,
      aprobaciones,
      isCuitFounded,
    ],
  )

  const verificarProgreso = useCallback(async cuit => {
    if (!cuit || recuperacionRealizadaRef.current) return

    try {
      const cuitLimpio = cuit.replace(/[^0-9]/g, "")
      const progreso = await recuperarProgresoOnboarding(cuitLimpio)

      if (progreso.tieneProgresoGuardado) {
        setProgresoRecuperado(progreso)
        setMostrarModalRecuperar(true)
      } else {
        recuperacionRealizadaRef.current = true
      }
    } catch (error) {
      console.error("Error verificando progreso:", error)
      recuperacionRealizadaRef.current = true
    }
  }, [])

  const aplicarProgresoRecuperado = useCallback(() => {
    if (!progresoRecuperado) return

    setCargandoRecuperacion(true)

    try {
      // Trackear recuperación de progreso
      tracking.trackRecuperacion("recuperar")

      if (progresoRecuperado.datosFormulario) {
        setValues(progresoRecuperado.datosFormulario)
      }

      const adicionales = progresoRecuperado.datosAdicionales || {}

      if (adicionales.documentosEstado)
        setDocumentosEstado(adicionales.documentosEstado)
      if (adicionales.kycCompletado !== undefined)
        setKYCCompletado(adicionales.kycCompletado)
      if (adicionales.resultadoKYC) setResultadoKYC(adicionales.resultadoKYC)
      if (adicionales.evaluacionCompleta)
        setEvaluacionCompleta(adicionales.evaluacionCompleta)
      if (adicionales.calificacionElegida)
        setCalificacionElegida(adicionales.calificacionElegida)
      if (adicionales.firmantes) setFirmantes(adicionales.firmantes)
      if (adicionales.estadosBiometria)
        setEstadosBiometria(adicionales.estadosBiometria)
      if (adicionales.firmantesCompletos !== undefined)
        setFirmantesCompletos(adicionales.firmantesCompletos)
      if (adicionales.garantias) setGarantias(adicionales.garantias)
      if (adicionales.garantiasCompletas !== undefined)
        setGarantiasCompletas(adicionales.garantiasCompletas)
      if (adicionales.aprobaciones) setAprobaciones(adicionales.aprobaciones)
      if (adicionales.isCuitFounded !== undefined)
        setIsCuitFounded(adicionales.isCuitFounded)

      setPasoActual(progresoRecuperado.pasoActual || 0)

      // Setear CUIT en tracking
      if (progresoRecuperado.cuit) {
        tracking.setSesionCuit(progresoRecuperado.cuit)
      }

      recuperacionRealizadaRef.current = true
      setMostrarModalRecuperar(false)
      setProgresoRecuperado(null)

      setSuccess("Progreso recuperado exitosamente")
    } catch (error) {
      console.error("Error aplicando progreso:", error)
      setError("Error al restaurar el progreso")
      tracking.trackError("Error al restaurar progreso", {
        error: error.message,
      })
    } finally {
      setCargandoRecuperacion(false)
    }
  }, [progresoRecuperado, setValues, setSuccess, setError, tracking])

  const handleDescartarProgreso = useCallback(async () => {
    const cuit = progresoRecuperado?.cuit || values?.titular?.cuit

    // Trackear descarte de progreso
    tracking.trackRecuperacion("descartar")

    if (cuit) {
      try {
        await descartarProgresoOnboarding(cuit)
      } catch (error) {
        console.error("Error descartando progreso:", error)
      }
    }

    recuperacionRealizadaRef.current = true
    setMostrarModalRecuperar(false)
    setProgresoRecuperado(null)
  }, [progresoRecuperado, values?.titular?.cuit, tracking])

  // Efectos de persistencia
  useEffect(() => {
    const cuit = values?.titular?.cuit
    if (cuit && cuit.length >= 11 && !recuperacionRealizadaRef.current) {
      verificarProgreso(cuit)
    }
  }, [values?.titular?.cuit, verificarProgreso])

  useEffect(() => {
    if (autoguardadoTimerRef.current) {
      clearInterval(autoguardadoTimerRef.current)
    }

    autoguardadoTimerRef.current = setInterval(() => {
      const cuit = values?.titular?.cuit
      if (cuit && cuit.length >= 11) {
        guardarProgreso()
      }
    }, INTERVALO_AUTOGUARDADO)

    return () => {
      if (autoguardadoTimerRef.current) {
        clearInterval(autoguardadoTimerRef.current)
      }
    }
  }, [guardarProgreso, values?.titular?.cuit])

  useEffect(() => {
    const cuit = values?.titular?.cuit
    if (cuit && cuit.length >= 11 && pasoActual > 0) {
      guardarProgreso()
    }
  }, [pasoActual])

  useEffect(() => {
    const handleBeforeUnload = () => {
      const cuit = values?.titular?.cuit
      if (cuit && cuit.length >= 11) {
        const datosAdicionales = {
          documentosEstado,
          kycCompletado,
          resultadoKYC,
          evaluacionCompleta,
          calificacionElegida,
          firmantes,
          estadosBiometria,
          firmantesCompletos,
          garantias,
          garantiasCompletas,
          aprobaciones,
          isCuitFounded,
        }

        navigator.sendBeacon &&
          navigator.sendBeacon(
            "/api/onboarding/progreso",
            JSON.stringify({
              cuit: cuit.replace(/[^0-9]/g, ""),
              pasoActual,
              nombrePaso: NOMBRES_PASOS[pasoActual],
              datosFormulario: values,
              datosAdicionales,
            }),
          )
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [
    values,
    pasoActual,
    documentosEstado,
    kycCompletado,
    resultadoKYC,
    evaluacionCompleta,
    calificacionElegida,
    firmantes,
    estadosBiometria,
    firmantesCompletos,
    garantias,
    garantiasCompletas,
    aprobaciones,
    isCuitFounded,
  ])

  // ============================================
  // Handlers con tracking integrado
  // ============================================

  // Handler cuando se busca/encuentra CUIT
  const handleCuitFound = useCallback(
    (cuit, encontrado, datos) => {
      setIsCuitFounded(encontrado)
      if (datos) setDatosDB(datos)

      // Trackear búsqueda de CUIT
      tracking.trackCuit(cuit, encontrado)
      tracking.setSesionCuit(cuit)
    },
    [tracking],
  )

  // Handler cuando se carga un documento
  const handleDocumentoCargado = useCallback(
    (tipoDocumento, exitoso) => {
      tracking.trackDocumento(tipoDocumento, exitoso)
    },
    [tracking],
  )

  // Handler cuando se completa KYC
  const handleKYCCompletado = useCallback(
    (completado, resultado) => {
      setKYCCompletado(completado)
      setResultadoKYC(resultado)

      if (resultado) {
        tracking.trackKYC(resultado)
      }
    },
    [tracking],
  )

  // Handler cuando se completa evaluación
  const handleEvaluacionCompletada = useCallback(
    (resultado, calificacion) => {
      setEvaluacionCompleta(resultado)
      setCalificacionElegida(calificacion)

      if (resultado) {
        tracking.trackEvaluacion(resultado.resultadofinal, calificacion)
      }
    },
    [tracking],
  )

  // Handler para extracción de estatuto
  const handleEstatutoExtraido = useCallback(
    (exitoso, cantidadFirmantes) => {
      tracking.trackEstatuto(exitoso, cantidadFirmantes)
    },
    [tracking],
  )

  // Handler para iniciar validación biométrica
  // const handleIniciarBiometria = useCallback((firmante) => {
  //   // Trackear inicio de biometría
  //   tracking.trackBiometria("EN_PROCESO", firmante.numeroDocumento)

  //   setEstadosBiometria(prev => ({
  //     ...prev,
  //     [firmante.numeroDocumento]: "EN_PROCESO"
  //   }))

  //   // TODO: Integrar con servicio de biometría real
  //   // Simulación de proceso
  //   setTimeout(() => {
  //     const aprobado = Math.random() > 0.1 // 90% de aprobación simulada
  //     const nuevoEstado = aprobado ? "APROBADA" : "RECHAZADA"

  //     setEstadosBiometria(prev => ({
  //       ...prev,
  //       [firmante.numeroDocumento]: nuevoEstado
  //     }))

  //     // Trackear resultado de biometría
  //     tracking.trackBiometria(nuevoEstado, firmante.numeroDocumento)

  //     if (aprobado) {
  //       setSuccess(`Biometría aprobada para ${firmante.nombre}`)
  //     } else {
  //       setError(`Biometría rechazada para ${firmante.nombre}`)
  //     }
  //   }, 3000)
  // }, [tracking, setSuccess, setError])

  const handleIniciarBiometria = useCallback(
    firmante => {
      // Abrir modal de biometría
      setModalBiometria({ open: true, firmante })

      // Marcar como en proceso
      setEstadosBiometria(prev => ({
        ...prev,
        [firmante.numeroDocumento]: "EN_PROCESO",
      }))

      // Trackear inicio
      tracking.trackBiometria("EN_PROCESO", firmante.numeroDocumento)
    },
    [tracking],
  )

  const handleResultadoBiometria = useCallback(
    (numeroDocumento, estado, resultado) => {
      setEstadosBiometria(prev => ({
        ...prev,
        [numeroDocumento]: estado,
      }))

      // Trackear resultado
      tracking.trackBiometria(estado, numeroDocumento)

      // Mostrar mensaje
      if (estado === "APROBADA") {
        setSuccess(
          `Biometría aprobada - Score: ${resultado?.scoreMatch?.toFixed(1) || "OK"}%`,
        )
      } else if (estado === "RECHAZADA") {
        setError(
          `Biometría rechazada: ${resultado?.mensaje || "Error en validación"}`,
        )
      }

      // Cerrar modal
      setModalBiometria({ open: false, firmante: null })
    },
    [tracking, setSuccess, setError],
  )

  // Handler para garantías
  const handleGarantiaChange = useCallback(
    (accion, garantia) => {
      tracking.trackGarantia(accion, garantia.tipo, garantia.monto)
    },
    [tracking],
  )

  // Handler para aprobaciones
  const handleAprobacionDecision = useCallback(
    (nivel, estado, motivo) => {
      tracking.trackAprobacion(nivel, estado, motivo)
    },
    [tracking],
  )

  // ============================================
  // Validaciones por paso
  // ============================================

  const checkValidateIdentificacion = () => isCuitFounded

  const errorIdentificacion = () => {
    if (!isCuitFounded) alert("Debe buscar y validar un cliente por CUIT")
  }

  const checkValidateDocumentacion = () => documentacionCompleta

  const errorDocumentacion = () => {
    alert("Debe cargar todos los documentos requeridos")
  }

  const checkValidateKYC = () => {
    return kycCompletado && resultadoKYC?.nivelRiesgoGlobal !== "CRITICO"
  }

  const errorKYC = () => {
    if (!kycCompletado) {
      alert("La verificación KYC/AML debe completarse")
    } else if (resultadoKYC?.nivelRiesgoGlobal === "CRITICO") {
      alert(
        "No se puede continuar: Cliente con riesgo crítico en verificación KYC/AML",
      )
    }
  }

  const checkValidateEvaluacion = () => {
    if (!evaluacionCompleta) return false
    if (evaluacionCompleta.resultadofinal !== "Aceptado") return false
    if (
      !evaluacionCompleta.calificacion ||
      evaluacionCompleta.calificacion.length === 0
    )
      return false
    return true
  }

  const errorEvaluacion = () => {
    if (!evaluacionCompleta) {
      alert("Debe completarse la evaluación crediticia")
      return
    }
    if (evaluacionCompleta.resultadofinal !== "Aceptado") {
      alert("La evaluación no fue aprobada. No se puede continuar.")
      return
    }
    alert("No hay calificación disponible para continuar")
  }

  const checkValidateFirmantes = () => {
    if (firmantes.length === 0) return false

    const firmantesConBiometria = firmantes.filter(f => f.requiereBiometria)
    if (firmantesConBiometria.length > 0) {
      const todosAprobados = firmantesConBiometria.every(
        f => estadosBiometria[f.numeroDocumento] === "APROBADA",
      )
      return todosAprobados
    }

    return true
  }

  const errorFirmantes = () => {
    if (firmantes.length === 0) {
      alert("Debe identificar al menos un firmante autorizado")
      return
    }

    const firmantesConBiometria = firmantes.filter(f => f.requiereBiometria)
    const pendientes = firmantesConBiometria.filter(
      f => estadosBiometria[f.numeroDocumento] !== "APROBADA",
    )

    if (pendientes.length > 0) {
      alert(
        `Faltan validaciones biométricas pendientes: ${pendientes.map(f => f.nombre).join(", ")}`,
      )
    }
  }

  const checkValidateGarantias = () => {
    return garantiasCompletas || garantias.some(g => g.tipo === "sin_garantia")
  }

  const errorGarantias = () => {
    alert(
      "Debe configurar garantías con cobertura suficiente o indicar operación quirografaria",
    )
  }

  const checkValidateAprobacion = () => true

  // ============================================
  // Handler de finalización
  // ============================================

  const handleComplete = async () => {
    if (
      !evaluacionCompleta ||
      evaluacionCompleta.resultadofinal !== "Aceptado"
    ) {
      alert("La evaluación debe estar aprobada para generar la solicitud.")
      return
    }

    if (resultadoKYC?.nivelRiesgoGlobal === "CRITICO") {
      alert(
        "No se puede generar solicitud: Cliente con riesgo crítico en verificación KYC/AML.",
      )
      return
    }

    if (firmantes.length === 0) {
      alert("Debe identificar al menos un firmante autorizado.")
      return
    }

    const nivelesAprobados = Object.values(aprobaciones).filter(
      a => a.estado === "aprobado" || a.estado === "condicionado",
    )
    if (nivelesAprobados.length === 0) {
      alert("La solicitud debe ser aprobada antes de generar.")
      return
    }

    const ultimaAprobacion = aprobaciones[Object.keys(aprobaciones).length]
    if (ultimaAprobacion?.estado === "rechazado") {
      alert("La solicitud fue rechazada y no puede generarse.")
      return
    }

    setSaving(true)

    const solicitudFinal = {
      ...evaluacionCompleta,
      resultadoKYC: resultadoKYC,
      documentacion: documentosEstado,
      firmantes: firmantes,
      estadosBiometria: estadosBiometria,
      garantias: garantias,
      aprobaciones: aprobaciones,
    }

    try {
      const response = await solicitarOfertaEvaluacion(solicitudFinal)

      const cuit = values?.titular?.cuit
      if (cuit) {
        await marcarProgresoCompletado(cuit)
      }

      // Trackear fin exitoso
      tracking.trackFin()

      setSuccess(`Solicitud generada exitosamente. ID: ${response.nro}`)
      history.push(`/success`)
    } catch (error) {
      setError(error)
      tracking.trackError("Error al generar solicitud", {
        error: error.message,
      })
    } finally {
      setSaving(false)
    }
  }

  // ============================================
  // Handler de cambio de tab con tracking
  // ============================================

  const tabChanged = ({ prevIndex, nextIndex }) => {
    console.log("Tab changed from", prevIndex, "to", nextIndex)

    // Trackear cambio de paso
    tracking.trackCambioDeTab(prevIndex, nextIndex)

    setPasoActual(nextIndex)
  }

  const handleAprobacionFinal = decision => {
    console.log("Aprobación final:", decision)
  }

  // ============================================
  // Props compartidos
  // ============================================

  const props = {
    formik,
    provincias,
    actividad,
    personeria,
    referidos,
    condicionIva,
    isCuitFounded,
    setIsCuitFounded,
    setDatosDB,
    asesorComercial,
    actividadEspecifica,
    situacionBCRA,
    tamanioEmpresa,
    tipoCanalComercializacion,
    sectorEconomico,
    originador,
    ciudades: [],
    isLoadingCiudades: false,
    esEvaluador: true,
    onCuitFound: handleCuitFound, // Callback para tracking
  }

  const montoCredito = evaluacionCompleta?.calificacion?.[0]?.monto || 0

  const datosSolicitud = {
    cliente: values.titular?.razonSocial || values.titular?.nombre,
    montoSolicitado: montoCredito,
    plazo: 12,
    tasa: 0,
    nivelRiesgo:
      resultadoKYC?.nivelRiesgoGlobal ||
      evaluacionCompleta?.scoring?.nivelRiesgo ||
      "MEDIO",
    scoreKYC: resultadoKYC?.scoreKYC || evaluacionCompleta?.scoring?.score || 0,
    cobertura:
      garantias.length > 0
        ? Math.round(
            (garantias.reduce((sum, g) => sum + (g.montoGarantizado || 0), 0) /
              montoCredito) *
              100,
          )
        : 0,
  }

  // ============================================
  // Template del título con indicador de guardado
  // ============================================

  const customTitleTemplate = () => (
    <div style={{ padding: "1rem", textAlign: "center" }}>
      {/* <h4 style={{ color: "#309e43", marginBottom: 0 }}>
        Onboarding Crediticio
      </h4> */}
      {/* <div style={{ fontSize: "0.75rem", marginTop: "0.5rem" }}>
        {guardandoProgreso && (
          <span className="text-muted">
            <span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />
            Guardando...
          </span>
        )}
        {!guardandoProgreso && ultimoGuardado && (
          <span className="text-success">
            <i className="bx bx-check me-1" />
            Guardado {ultimoGuardado.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div> */}
    </div>
  )

  // ============================================
  // Render
  // ============================================

  return (
    <div className="onboarding-container">
      <ModalRecuperarProgreso
        isOpen={mostrarModalRecuperar}
        toggle={() => setMostrarModalRecuperar(false)}
        progreso={progresoRecuperado}
        onRecuperar={aplicarProgresoRecuperado}
        onDescartar={handleDescartarProgreso}
        cargando={cargandoRecuperacion}
      />

      <div className="form-wizard-container">
        <FormWizard
          ref={formWizardRef}
          stepSize="xs"
          onComplete={handleComplete}
          title={customTitleTemplate()}
          color="#309e43"
          nextButtonText="Siguiente"
          backButtonText="Anterior"
          finishButtonText="Generar Solicitud"
          onTabChange={tabChanged}
        >
          {/* PASO 1: Identificación */}
          <FormWizard.TabContent title="Identificación" icon="ti-user">
            <Card className="mb-0">
              <CardBody>
                <Titular props={props} />
              </CardBody>
            </Card>
          </FormWizard.TabContent>

          {/* PASO 2: Documentación */}
          <FormWizard.TabContent
            title="Documentación"
            icon="ti-files"
            isValid={checkValidateIdentificacion()}
            validationError={errorIdentificacion}
          >
            <Card className="mb-0">
              <CardBody>
                <Documentacion
                  tipoSociedad={values.titular?.tipoSociedad}
                  documentosEstado={documentosEstado}
                  setDocumentosEstado={setDocumentosEstado}
                  setDocumentacionCompleta={setDocumentacionCompleta}
                  onDocumentoCargado={handleDocumentoCargado}
                />
              </CardBody>
            </Card>
          </FormWizard.TabContent>

          {/* PASO 3: KYC / AML */}
          <FormWizard.TabContent
            title="KYC / AML"
            icon="ti-shield"
            isValid={checkValidateDocumentacion()}
            validationError={errorDocumentacion}
          >
            <Card className="mb-0">
              <CardBody>
                <KYC
                  cuit={values.titular?.cuit}
                  denominacion={
                    values.titular?.razonSocial || values.titular?.nombre
                  }
                  setKYCCompletado={setKYCCompletado}
                  setResultadoKYC={setResultadoKYC}
                />
              </CardBody>
            </Card>
          </FormWizard.TabContent>

          {/* PASO 4: Evaluación */}
          <FormWizard.TabContent
            title="Evaluación"
            icon="ti-stats-up"
            isValid={checkValidateKYC()}
            validationError={errorKYC}
          >
            {saving && (
              <div className="text-center mb-5">
                <h4>
                  Procesando...
                  <br />
                  <i className="bx bx-loader-alt bx-spin text-primary display-3" />
                </h4>
              </div>
            )}
            <div style={{ display: saving ? "none" : "block" }}>
              <Evaluacion
                props={props}
                cuit={values.titular?.cuit}
                setCalificacionElegida={setCalificacionElegida}
                setEvaluacionCompleta={resultado => {
                  handleEvaluacionCompletada(resultado, calificacionElegida)
                }}
              />
            </div>
          </FormWizard.TabContent>

          {/* PASO 5: Firmantes */}
          <FormWizard.TabContent
            title="Firmantes"
            icon="ti-id-badge"
            isValid={checkValidateEvaluacion()}
            validationError={errorEvaluacion}
          >
            <Card className="mb-0">
              <CardBody>
                <Firmantes
                  cuit={values.titular?.cuit}
                  razonSocial={
                    values.titular?.razonSocial || values.titular?.nombre
                  }
                  tipoSociedad={values.titular?.tipoSociedad}
                  firmantes={firmantes}
                  setFirmantes={setFirmantes}
                  estadosBiometria={estadosBiometria}
                  setEstadosBiometria={setEstadosBiometria}
                  setFirmantesCompletos={setFirmantesCompletos}
                  onIniciarBiometria={handleIniciarBiometria}
                  onEstatutoExtraido={handleEstatutoExtraido}
                />
              </CardBody>
            </Card>
          </FormWizard.TabContent>

          {/* PASO 6: Garantías */}
          <FormWizard.TabContent
            title="Garantías"
            icon="ti-home"
            isValid={checkValidateFirmantes()}
            validationError={errorFirmantes}
          >
            <Card className="mb-0">
              <CardBody>
                <Garantias
                  montoCredito={montoCredito}
                  garantias={garantias}
                  setGarantias={setGarantias}
                  setGarantiasCompletas={setGarantiasCompletas}
                  onGarantiaChange={handleGarantiaChange}
                />
              </CardBody>
            </Card>
          </FormWizard.TabContent>

          {/* PASO 7: Aprobación */}
          <FormWizard.TabContent
            title="Aprobación"
            icon="ti-check-box"
            isValid={checkValidateGarantias()}
            validationError={errorGarantias}
          >
            <Card className="mb-0">
              <CardBody>
                <Aprobacion
                  datosSolicitud={datosSolicitud}
                  aprobaciones={aprobaciones}
                  setAprobaciones={setAprobaciones}
                  onAprobacionFinal={handleAprobacionFinal}
                  onAprobacionDecision={handleAprobacionDecision}
                />
              </CardBody>
            </Card>
          </FormWizard.TabContent>
        </FormWizard>
      </div>

      <ModalBiometria
        isOpen={modalBiometria.open}
        toggle={() => setModalBiometria({ open: false, firmante: null })}
        firmante={modalBiometria.firmante}
        cuit={values.titular?.cuit}
        onResultado={handleResultadoBiometria}
      />
    </div>
  )
}
